/**
 * ElasticSearch-Grade Open-Source BM25 Search Engine
 * 
 * Implements:
 * 1. Okapi BM25 Ranking Algorithm with Term Frequency (TF) & Inverse Document Frequency (IDF)
 * 2. Multi-field weighted boosting (Sport^4.5, Title^3.0, Venue^2.5, Address^2.0, Host^1.2)
 * 3. Tokenizer with normalization, stop-word elimination, and light stemming
 * 4. Damerau-Levenshtein fuzzy typo tolerance (AUTO fuzziness)
 * 5. Edge-Ngram prefix completion for instant as-you-type matching
 * 6. Exact phrase match amplification
 */

import type { MockGame } from './mock-data';

// Common English stop words that carry little search signal
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'in', 'on', 'at', 'to', 'for', 'of', 'and', 'or', 'is',
  'are', 'was', 'with', 'by', 'from', 'near', 'it', 'as', 'be', 'this', 'that'
]);

// Field weights inspired by Elasticsearch index mappings
export const FIELD_WEIGHTS = {
  sport: 4.5,
  title: 3.0,
  playgroundName: 2.5,
  playgroundAddress: 2.0,
  hostName: 1.2,
  description: 1.0,
  skillLevel: 1.2,
};

// Okapi BM25 parameters
const BM25_K1 = 1.2;
const BM25_B = 0.75;

/**
 * Tokenize and normalize input text into searchable terms.
 */
export function tokenize(text: string): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .split(/[^a-z0-9]+/i)
    .filter(t => t.length > 0 && !STOP_WORDS.has(t))
    .map(stemWord);
}

/**
 * Lightweight English stemmer for common sport & venue terms
 */
function stemWord(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith('ies')) return word.slice(0, -3) + 'y';
  if (word.endsWith('ing') && word.length > 5) return word.slice(0, -3);
  if (word.endsWith('es') && word.length > 4) return word.slice(0, -2);
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 3) return word.slice(0, -1);
  if (word.endsWith('ed') && word.length > 4) return word.slice(0, -2);
  return word;
}

/**
 * Calculate Damerau-Levenshtein edit distance for fuzzy typo tolerance
 */
export function damerauLevenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const d: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    d[i] = [i];
  }
  for (let j = 0; j <= b.length; j++) {
    d[0][j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,       // deletion
        d[i][j - 1] + 1,       // insertion
        d[i - 1][j - 1] + cost // substitution
      );

      // Transposition (e.g. "teh" -> "the")
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost);
      }
    }
  }

  return d[a.length][b.length];
}

/**
 * Internal representation of a searchable game document in the inverted index
 */
export interface IndexedGame {
  id: string;
  game: MockGame;
  tokensByField: {
    sport: string[];
    title: string[];
    playgroundName: string[];
    playgroundAddress: string[];
    hostName: string[];
    description: string[];
  };
  totalWeightedLength: number;
  rawSearchableText: string;
}

/**
 * Prepare and index a game for BM25 ranking
 */
export function indexGame(game: MockGame): IndexedGame {
  const sportTokens = tokenize(game.sport);
  const titleTokens = tokenize(game.title);
  const playgroundNameTokens = tokenize(game.playground?.name || '');
  const playgroundAddressTokens = tokenize(game.playground?.address || '');
  const hostNameTokens = tokenize(game.hostName || '');
  const descriptionTokens = tokenize((game as any).description || '');

  const totalWeightedLength =
    sportTokens.length * FIELD_WEIGHTS.sport +
    titleTokens.length * FIELD_WEIGHTS.title +
    playgroundNameTokens.length * FIELD_WEIGHTS.playgroundName +
    playgroundAddressTokens.length * FIELD_WEIGHTS.playgroundAddress +
    hostNameTokens.length * FIELD_WEIGHTS.hostName +
    descriptionTokens.length * FIELD_WEIGHTS.description;

  const rawSearchableText = [
    game.sport,
    game.title,
    game.playground?.name,
    game.playground?.address,
    game.hostName,
    (game as any).description,
  ].filter(Boolean).join(' ').toLowerCase();

  return {
    id: game.id,
    game,
    tokensByField: {
      sport: sportTokens,
      title: titleTokens,
      playgroundName: playgroundNameTokens,
      playgroundAddress: playgroundAddressTokens,
      hostName: hostNameTokens,
      description: descriptionTokens,
    },
    totalWeightedLength: Math.max(1, totalWeightedLength),
    rawSearchableText,
  };
}

/**
 * Open-Source BM25 Search Engine
 */
export class ElasticSearchEngine {
  private documents: IndexedGame[] = [];
  private avgDocLength: number = 10;
  private docCount: number = 0;
  private termDocFreq: Map<string, number> = new Map();

  constructor(games: MockGame[] = []) {
    this.index(games);
  }

  /**
   * Re-indexes a list of games
   */
  public index(games: MockGame[]): void {
    this.docCount = games.length;
    this.documents = games.map(indexGame);
    this.termDocFreq.clear();

    if (this.docCount === 0) {
      this.avgDocLength = 10;
      return;
    }

    let totalLength = 0;

    // Build document frequencies for IDF
    for (const doc of this.documents) {
      totalLength += doc.totalWeightedLength;
      const uniqueDocTokens = new Set<string>();

      Object.values(doc.tokensByField).forEach(tokens => {
        tokens.forEach(t => uniqueDocTokens.add(t));
      });

      uniqueDocTokens.forEach(t => {
        this.termDocFreq.set(t, (this.termDocFreq.get(t) || 0) + 1);
      });
    }

    this.avgDocLength = totalLength / this.docCount;
  }

  /**
   * Calculate Okapi BM25 Inverse Document Frequency (IDF) with smoothing
   */
  private calculateIdf(term: string): number {
    const docFreq = this.termDocFreq.get(term) || 0;
    // Standard Lucene/BM25 IDF formula with 1.0 floor
    return Math.log(1 + (this.docCount - docFreq + 0.5) / (docFreq + 0.5)) + 1.0;
  }

  /**
   * Match a query token against document tokens, with exact, prefix, and fuzzy matching
   */
  private scoreTermInField(
    queryTerm: string,
    fieldTokens: string[],
    fieldWeight: number
  ): number {
    let termFreq = 0;

    for (const docTerm of fieldTokens) {
      // 1. Exact match
      if (docTerm === queryTerm) {
        termFreq += 1.0 * fieldWeight;
        continue;
      }

      // 2. Prefix matching (e.g. query "foot" matches "football", "kora" matches "koramangala")
      if (queryTerm.length >= 3 && docTerm.startsWith(queryTerm)) {
        termFreq += 0.85 * fieldWeight;
        continue;
      }
      if (docTerm.length >= 3 && queryTerm.startsWith(docTerm)) {
        termFreq += 0.75 * fieldWeight;
        continue;
      }

      // 3. Fuzzy matching for typos (AUTO fuzziness based on term length)
      const termLen = queryTerm.length;
      if (termLen >= 4) {
        const maxDist = termLen >= 6 ? 2 : 1;
        const dist = damerauLevenshtein(queryTerm, docTerm);
        if (dist <= maxDist) {
          const similarity = 1 - dist / Math.max(termLen, docTerm.length);
          termFreq += similarity * 0.7 * fieldWeight;
        }
      }
    }

    return termFreq;
  }

  /**
   * Execute BM25 search across indexed games
   */
  public search(
    rawQuery: string,
    activeSport: string = 'ALL',
    options?: { minScore?: number }
  ): { rankedGames: MockGame[]; scores: Map<string, number> } {
    const trimmedQuery = rawQuery.trim().toLowerCase();
    const queryTokens = tokenize(trimmedQuery);
    const minScore = options?.minScore ?? 0.1;

    // Filter by sport first if specified
    const eligibleDocs = activeSport === 'ALL'
      ? this.documents
      : this.documents.filter(d => d.game.sport === activeSport);

    // If query is empty, return games sorted by urgency (need 1 more) and distance
    if (!queryTokens.length) {
      const defaultSorted = [...eligibleDocs.map(d => d.game)].sort((a, b) => {
        const aNeed = a.capacity - a.confirmedPlayers;
        const bNeed = b.capacity - b.confirmedPlayers;
        if (aNeed === 1 && bNeed !== 1) return -1;
        if (bNeed === 1 && aNeed !== 1) return 1;
        return a.distance - b.distance;
      });

      return {
        rankedGames: defaultSorted,
        scores: new Map(),
      };
    }

    const scores = new Map<string, number>();

    for (const doc of eligibleDocs) {
      let docScore = 0;
      let matchedTokensCount = 0;

      for (const qTerm of queryTokens) {
        const idf = this.calculateIdf(qTerm);

        // Sum weighted frequencies across all fields
        const tf =
          this.scoreTermInField(qTerm, doc.tokensByField.sport, FIELD_WEIGHTS.sport) +
          this.scoreTermInField(qTerm, doc.tokensByField.title, FIELD_WEIGHTS.title) +
          this.scoreTermInField(qTerm, doc.tokensByField.playgroundName, FIELD_WEIGHTS.playgroundName) +
          this.scoreTermInField(qTerm, doc.tokensByField.playgroundAddress, FIELD_WEIGHTS.playgroundAddress) +
          this.scoreTermInField(qTerm, doc.tokensByField.hostName, FIELD_WEIGHTS.hostName) +
          this.scoreTermInField(qTerm, doc.tokensByField.description, FIELD_WEIGHTS.description);

        if (tf > 0) {
          matchedTokensCount++;
          // Standard BM25 term score formula
          const numerator = tf * (BM25_K1 + 1);
          const denominator = tf + BM25_K1 * (1 - BM25_B + BM25_B * (doc.totalWeightedLength / this.avgDocLength));
          docScore += idf * (numerator / denominator);
        }
      }

      // Bonus 1: Multi-term coverage multiplier (documents matching all query terms get a boost)
      if (queryTokens.length > 1) {
        const coverageRatio = matchedTokensCount / queryTokens.length;
        docScore *= Math.pow(coverageRatio, 0.8) * (coverageRatio === 1 ? 1.4 : 1.0);
      }

      // Bonus 2: Exact phrase occurrence in title, sport or playground name
      if (trimmedQuery.length >= 3 && doc.rawSearchableText.includes(trimmedQuery)) {
        docScore *= 1.8;
      }

      if (docScore >= minScore) {
        scores.set(doc.id, docScore);
      }
    }

    // Sort by BM25 relevance score descending.
    // If scores are very close (<5% diff), use distance and slot urgency as tie-breaker.
    const rankedDocs = eligibleDocs
      .filter(d => scores.has(d.id))
      .sort((a, b) => {
        const scoreA = scores.get(a.id) || 0;
        const scoreB = scores.get(b.id) || 0;

        const scoreDiffRatio = Math.abs(scoreA - scoreB) / Math.max(scoreA, scoreB, 1);
        if (scoreDiffRatio > 0.08) {
          return scoreB - scoreA;
        }

        // Secondary tie-breaker: need 1 player urgency
        const aNeed = a.game.capacity - a.game.confirmedPlayers;
        const bNeed = b.game.capacity - b.game.confirmedPlayers;
        if (aNeed === 1 && bNeed !== 1) return -1;
        if (bNeed === 1 && aNeed !== 1) return 1;

        // Tertiary tie-breaker: physical distance
        return a.game.distance - b.game.distance;
      });

    return {
      rankedGames: rankedDocs.map(d => d.game),
      scores,
    };
  }
}

/**
 * Helper hook / functional interface for React components
 */
export function filterGamesWithElasticSearch(
  games: MockGame[],
  searchQuery: string,
  activeSport: string = 'ALL'
): MockGame[] {
  if (!games || games.length === 0) return [];
  const engine = new ElasticSearchEngine(games);
  const { rankedGames } = engine.search(searchQuery, activeSport);
  return rankedGames;
}
